import { useRef } from 'react';

import styled from 'styled-components';

import { CopyButton, CopyButtonProps } from '@components/copy-button';
import { FormError } from '@components/form-error';
import { Stack } from '@components/stack';
import { Text } from '@components/text';
import { TextField, TextFieldProps } from '@components/text-field';
import { useRenderedDimensions } from '@hooks/use-rendered-dimensions';
import { theme } from '@styles/theme';
import { pxToRem } from '@utils/converters';

export type PrefixedPostfixedTextFieldProps = TextFieldProps & {
  prefix?: string;
  postfix?: string;
  label?: string;
  hideCopyButton?: boolean;
  copyLabel?: CopyButtonProps['copyLabel'];
};

export const PrefixedPostfixedTextField = ({
  prefix = '',
  postfix = '',
  copyLabel,
  label,
  hideCopyButton = false,
  ...props
}: PrefixedPostfixedTextFieldProps) => {
  const { error, helpText, ...rest } = props;
  const inputRef = useRef<HTMLInputElement>(null);
  const { width: prefixWidth } = useRenderedDimensions(() => <Text text={prefix} />, [prefix]);
  const { width: postfixWidth } = useRenderedDimensions(() => <Text text={postfix} />, [postfix]);

  const handleCopy = () => {
    const text = inputRef.current?.value ?? '';
    return `${prefix}${text}${postfix}`;
  };

  const hasPrefix = Boolean(prefix);
  const hasPostfix = Boolean(postfix);

  return (
    <Stack space={1}>
      {label ? <Text font="semibold14" text={label} /> : null}
      <Stack orientation="horizontal" justify="space-between" space={2}>
        <Container
          orientation="horizontal"
          data-has-prefix={hasPrefix}
          data-has-postfix={hasPostfix}
        >
          {hasPrefix && (
            <TextField
              isDisabled
              label={label ?? ''}
              hideLabel
              value={prefix}
              style={{ width: prefixWidth }}
            />
          )}
          <TextField ref={inputRef} hideLabel label={label ?? ''} {...rest} />
          {hasPostfix && (
            <TextField
              isDisabled
              label={label ?? ''}
              hideLabel
              value={postfix}
              style={{ width: postfixWidth }}
            />
          )}
        </Container>
        {!hideCopyButton ? <CopyButton getText={handleCopy} copyLabel={copyLabel} /> : null}
      </Stack>
      <FormError text={error} />
      {helpText ? <Text font="regular14" color="textHelper" text={helpText} /> : null}
    </Stack>
  );
};

const Container = styled(Stack).attrs({
  orientation: 'horizontal',
  justify: 'space-between',
  alignItems: 'flex-end',
})(() => {
  const prefixPostfixStyles = (side: 'left' | 'right') => ({
    backgroundColor: theme.color.uiTrans10,
    [side === 'left' ? 'borderRightColor' : 'borderLeftColor']: 'transparent',
    [side === 'left' ? 'borderTopRightRadius' : 'borderTopLeftRadius']: pxToRem(0),
    [side === 'left' ? 'borderBottomRightRadius' : 'borderBottomLeftRadius']: pxToRem(0),
    input: { color: theme.color.textSecondary },
    svg: { display: 'none', width: pxToRem(0) },
  });

  const inputStyles = (side: 'left' | 'right') => ({
    [side === 'left' ? 'marginLeft' : 'marginRight']: pxToRem(-1),
    [side === 'left' ? 'borderTopLeftRadius' : 'borderTopRightRadius']: pxToRem(0),
    [side === 'left' ? 'borderBottomLeftRadius' : 'borderBottomRightRadius']: pxToRem(0),
  });

  return {
    width: '100%',
    '.input-container': { borderColor: theme.color.uiTrans10 },

    // Middle input should flex to fill remaining space
    '> *:not(:first-child):not(:last-child), > *:only-child': { flex: 1, minWidth: 0 },
    // Ensure input flexes in 2‑segment cases
    '&[data-has-prefix="true"]:not([data-has-postfix="true"]) > :last-child': {
      flex: 1,
      minWidth: 0,
    },
    '&[data-has-postfix="true"]:not([data-has-prefix="true"]) > :first-child': {
      flex: 1,
      minWidth: 0,
    },

    // Prefix affix and input seam
    '&[data-has-prefix="true"] > :first-child .input-container': prefixPostfixStyles('left'),
    '&[data-has-prefix="true"] > :nth-child(2) .input-container': inputStyles('left'),

    // Postfix affix and input seam
    '&[data-has-postfix="true"] > :last-child .input-container': prefixPostfixStyles('right'),
    '&[data-has-postfix="true"] > :nth-last-child(2) .input-container': inputStyles('right'),
  };
});
